import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, Euro, FileText, Download } from 'lucide-react';
import { format } from 'date-fns';
import { fmtDate } from '@/lib/dateFmt';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PreventiviPage() {
  const [preventivi, setPreventivi] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ cantiere_id: '', client_name: '', amount: '', description: '', valid_until: '', voci: [] });
  const [newVoce, setNewVoce] = useState({ descrizione: '', importo: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [prevRes, cantRes] = await Promise.all([
        axios.get(`${API}/preventivi`, { withCredentials: true }),
        axios.get(`${API}/cantieri`, { withCredentials: true })
      ]);
      setPreventivi(prevRes.data);
      setCantieri(cantRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally { setLoading(false); }
  };

  const addVoce = () => {
    if (!newVoce.descrizione || !newVoce.importo) return;
    const voci = [...formData.voci, { ...newVoce, importo: parseFloat(newVoce.importo) }];
    const total = voci.reduce((sum, v) => sum + v.importo, 0);
    setFormData({ ...formData, voci, amount: total.toString() });
    setNewVoce({ descrizione: '', importo: '' });
  };

  const removeVoce = (idx) => {
    const voci = formData.voci.filter((_, i) => i !== idx);
    const total = voci.reduce((sum, v) => sum + v.importo, 0);
    setFormData({ ...formData, voci, amount: total.toString() });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/preventivi`, { ...formData, amount: parseFloat(formData.amount || 0) }, { withCredentials: true });
      toast.success('Preventivo creato');
      setDialogOpen(false);
      setFormData({ cantiere_id: '', client_name: '', amount: '', description: '', valid_until: '', voci: [] });
      fetchData();
    } catch (error) { toast.error('Errore nel salvataggio'); }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await axios.put(`${API}/preventivi/${id}?status=${status}`, {}, { withCredentials: true });
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare il preventivo?')) return;
    try {
      await axios.delete(`${API}/preventivi/${id}`, { withCredentials: true });
      toast.success('Eliminato');
      fetchData();
    } catch (error) { toast.error('Errore'); }
  };

  const handleDownloadPdf = async (id, clientName) => {
    try {
      const res = await axios.get(`${API}/preventivi/${id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Preventivo_${clientName.replace(/\s/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch { toast.error('Errore download PDF'); }
  };

  const statusColors = { inviato: 'bg-blue-100 text-blue-800 border-blue-200', accettato: 'bg-emerald-100 text-emerald-800 border-emerald-200', rifiutato: 'bg-red-100 text-red-800 border-red-200' };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="preventivi-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Preventivi</h1>
          <p className="text-slate-500 mt-1">Crea e gestisci i preventivi per i clienti</p>
        </div>
        <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => setDialogOpen(true)} data-testid="add-preventivo-btn">
          <Plus className="h-4 w-4 mr-2" />Nuovo Preventivo
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cliente</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cantiere</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Descrizione</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Importo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Valido Fino</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Stato</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preventivi.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500"><FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />Nessun preventivo</TableCell></TableRow>
              ) : preventivi.map((p) => (
                <TableRow key={p.id} className="border-slate-100">
                  <TableCell className="font-medium">{p.client_name}</TableCell>
                  <TableCell className="text-slate-600">{cantieri.find(c => c.id === p.cantiere_id)?.name || '-'}</TableCell>
                  <TableCell className="max-w-xs truncate text-slate-600">{p.description}</TableCell>
                  <TableCell className="font-medium"><Euro className="h-3 w-3 inline mr-0.5" />{p.amount?.toLocaleString('it-IT', {minimumFractionDigits: 2})}</TableCell>
                  <TableCell className="text-slate-600">{fmtDate(p.valid_until, 'dd-MM-yyyy')}</TableCell>
                  <TableCell>
                    <Select value={p.status} onValueChange={(v) => handleStatusUpdate(p.id, v)}>
                      <SelectTrigger className="w-28 h-8"><Badge className={statusColors[p.status]} variant="outline">{p.status}</Badge></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inviato">Inviato</SelectItem>
                        <SelectItem value="accettato">Accettato</SelectItem>
                        <SelectItem value="rifiutato">Rifiutato</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleDownloadPdf(p.id, p.client_name)} className="text-blue-600 hover:text-blue-800" title="Scarica PDF" data-testid={`download-pdf-${p.id}`}><Download className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo Preventivo</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Cliente</Label><Input value={formData.client_name} onChange={(e) => setFormData({...formData, client_name: e.target.value})} required placeholder="Nome cliente" data-testid="preventivo-client-input" /></div>
              <div className="space-y-2"><Label>Valido Fino Al</Label><Input type="date" value={formData.valid_until} onChange={(e) => setFormData({...formData, valid_until: e.target.value})} required data-testid="preventivo-date-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>Cantiere (opzionale)</Label>
              <Select value={formData.cantiere_id} onValueChange={(v) => setFormData({...formData, cantiere_id: v})}>
                <SelectTrigger><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                <SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Descrizione Generale</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required placeholder="Descrizione del preventivo" data-testid="preventivo-desc-input" /></div>
            
            {/* Voci */}
            <div className="space-y-3">
              <Label className="font-semibold">Voci del Preventivo</Label>
              {formData.voci.map((v, idx) => (
                <div key={`${v.descrizione}-${v.importo}-${idx}`} className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-200">
                  <span className="text-sm">{v.descrizione}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">€{v.importo.toLocaleString('it-IT', {minimumFractionDigits: 2})}</span>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => removeVoce(idx)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
              <div className="flex gap-2">
                <Input placeholder="Descrizione voce" value={newVoce.descrizione} onChange={(e) => setNewVoce({...newVoce, descrizione: e.target.value})} className="flex-1" data-testid="voce-desc-input" />
                <Input type="number" step="0.01" placeholder="€" value={newVoce.importo} onChange={(e) => setNewVoce({...newVoce, importo: e.target.value})} className="w-28" data-testid="voce-importo-input" />
                <Button type="button" variant="outline" onClick={addVoce} data-testid="add-voce-btn"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">Totale: <span className="font-bold text-lg">€{parseFloat(formData.amount || 0).toLocaleString('it-IT', {minimumFractionDigits: 2})}</span></p>
            </div>

            <div className="space-y-2">
              <Label>Oppure importo manuale (€)</Label>
              <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} placeholder="0.00" data-testid="preventivo-amount-input" />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-preventivo-btn">Crea Preventivo</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
